import { jwtDecode } from 'jwt-decode';
import { get, omit, set } from 'lodash';
import { all, call, delay, getContext, put, takeEvery } from 'redux-saga/effects';
import { AuthTokenFields, UserType } from '@lendi/core-constants';
import { Session } from '@lendi/lala-react';

import { FIELD_PATHS, REDUX_STORE } from '@funnels/constants';
import {
  CreateApplicationAction,
  FetchApplicationsAction,
  ServerApplicationUpdate,
  Token,
  UpdateApplicationAction,
} from '@funnels/types';
import { datadogUtil } from '@funnels/utils';

import { getApplicationId, waitForAuth } from '../_common';
import {
  createApplication,
  createApplicationFail,
  createApplicationSuccess,
  fetchApplicationsFail,
  fetchApplicationsSuccess,
  setApplicationError,
  updateApplicationAbort,
  updateApplicationFail,
  updateApplicationSuccess,
} from './actions';
import ApplicationsAPI from './api';

export const refreshSession = (session: Session, newApplicationId: string): Promise<string> =>
  session.refresh(undefined, newApplicationId);
export function* createApplicationWorker(action: CreateApplicationAction): any {
  try {
    yield waitForAuth();

    const { clientId, isLocked, ...application } = action.application;
    const response = yield call(ApplicationsAPI.create, application);

    const session: Session = yield getContext('session');
    let jwt: Token = jwtDecode(session.token!);
    if (
      // this should - at least - be an empty array for the token to be the new format
      Array.isArray(jwt[AuthTokenFields.AuthApplications]) && // TODO: delete this line after every token is guaranteed v2
      // only refresh team tokens
      jwt[AuthTokenFields.Type] === UserType.Team
    ) {
      const newApplicationId = response.data.id;
      let it = 0;
      while (!jwt[AuthTokenFields.AuthApplications]?.includes(newApplicationId)) {
        /*
        Sleep in ms:
            0 // don't wait on the first run
          109
          240 // re-try twice (total 3 times), before the user notices (sub 500ms)
          395
          578
          792
          1042
          1333
          1671
          2000 // clamp to 2s max
          2000
          ...
        */
        yield delay(Math.min(Math.floor(100 * it * Math.pow(100, it / 50)), 2000));
        const token = yield call(refreshSession, session, newApplicationId);
        jwt = jwtDecode(token);
        it++;
      }
    }

    yield put(createApplicationSuccess(clientId, response.data.id));
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      application: { clientId },
    } = action;
    yield all([put(createApplicationFail(clientId)), put(setApplicationError(e.response?.data || e.message))]);
  }
}

export function* fetchApplicationsWorker(action: FetchApplicationsAction): any {
  try {
    yield waitForAuth();

    const { applicationId, customerId } = action;
    let response;

    if (applicationId) {
      response = yield call(ApplicationsAPI.fetch, applicationId);
      // We need to do this because single application fetch doesn't return the object in an array
      response.data = [response.data];
    } else {
      response = yield call(ApplicationsAPI.fetchAll, customerId);
    }

    const applications = response.data.map((application: any) => omit(application, ['brand', 'meta']));

    yield put(fetchApplicationsSuccess(applications));
  } catch (e: any) {
    datadogUtil.addError(e);
    yield all([put(fetchApplicationsFail()), put(setApplicationError(e.response?.data || e.message))]);
  }
}

export function* updateApplicationWorker(action: UpdateApplicationAction): any {
  try {
    yield waitForAuth();

    const applicationId = yield getApplicationId(action.application.clientId, action.application.id);
    const application = omit(action.application, [
      'clientId',
      'createdAt',
      'id',
      'isDeleted',
      'isDeidentified',
      'deidentificationMeta',
      'updatedAt',
      'isLocked',
    ]) as ServerApplicationUpdate;

    // For whatever reason, the creation failed so we convert this update to a create
    if (!applicationId) {
      yield all([put(updateApplicationAbort()), put(createApplication(action.application))]);
    } else {
      // make sure updated application has brokerPrimaryPurpose value https://creditandfinance.atlassian.net/browse/FUNNEL-3986
      set(
        { applications: [application] },
        FIELD_PATHS.APPLICATION_BROKER_PRIMARY_PURPOSE(0),
        get(
          { applications: [application] },
          FIELD_PATHS.APPLICATION_BROKER_PRIMARY_PURPOSE(0),
          get({ applications: [application] }, FIELD_PATHS.APPLICATION_PRIMARY_PURPOSE(0))
        )
      );
      yield call(ApplicationsAPI.update, applicationId, application);
      yield put(updateApplicationSuccess());
    }
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      application: { clientId },
    } = action;
    yield all([put(updateApplicationFail(clientId)), put(setApplicationError(e.response?.data || e.message))]);
  }
}

export function* applicationsWatcher() {
  yield takeEvery(REDUX_STORE.APPLICATIONS.ACTION_TYPES.CREATE_APPLICATION, createApplicationWorker);
  yield takeEvery(REDUX_STORE.APPLICATIONS.ACTION_TYPES.FETCH_APPLICATIONS, fetchApplicationsWorker);
  yield takeEvery(REDUX_STORE.APPLICATIONS.ACTION_TYPES.UPDATE_APPLICATION, updateApplicationWorker);
}

export default applicationsWatcher;
